import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { FundInvestments } from '../../services/fund-investments/fund-investments';
import { ROUTE_PATHS } from '../../utils/route-paths.const';
import { FUND_DETAILS_TABS } from '../../constants/tab-names.const';

/**
 * Guards the add-restricted-units page: only funds with at least one succeeded
 * investment may restrict units. In-app navigation passes `bypassGuard` (the
 * button already enforces the same rule); this protects direct/deep links.
 */
export const restrictedUnitsGuard: CanActivateFn = (
  route
): Observable<boolean> => {
  const fundInvestmentsService = inject(FundInvestments);
  const router = inject(Router);
  const fundUuid = route.paramMap.get('fundUuid');

  // Fall back to the fund's main-details tab: without a succeeded investment the
  // Unit Management tab is hidden, so we can't send the user back there.
  const navigateToFundDetails = (): void => {
    router.navigate(
      [ROUTE_PATHS.FUND_MANAGEMENT, fundUuid, ROUTE_PATHS.DETAILS],
      { queryParams: { tab: FUND_DETAILS_TABS.MAIN_DETAILS } }
    );
  };

  if (!fundUuid) {
    router.navigate([ROUTE_PATHS.ROOT]);
    return of(false);
  }

  const navigation = router.currentNavigation();
  const bypassGuard = navigation?.extras?.state?.['bypassGuard'];
  if (bypassGuard) {
    return of(true);
  }

  // hasSucceededInvestment already resolves load errors to `false`.
  return fundInvestmentsService.hasSucceededInvestment(fundUuid).pipe(
    map(hasSuccessfulInvestment => {
      if (!hasSuccessfulInvestment) {
        navigateToFundDetails();
        return false;
      }
      return true;
    })
  );
};
